import { useMemo, useState } from "react"
import { OpenInNew } from "@mui/icons-material"
import { Box, Button } from "@mui/material"
import { useAnalytics } from "connected-components/analytics"
import { formatValueForDisplay, getColumnType, isNumberFormat, parseValue } from "../../helpers/formatting"
import { VALUE_FORMATS } from "helpers/ai"
import { first, isNumber } from "lodash"
import pluralize from "pluralize"
import { AI_TEAMMATE_EVENTS } from "../../analytics"
import { isPivotable } from "../../helpers"
import { TABLE_ARTIFACT } from "../../helpers/shapes"
import { useAiTeammateContext } from "../../hooks/context"
import FullSizeTable from "./components/full-size-table"
import PreviewTable from "./components/preview-table"
import "./style.scss"

export default function TableArtifact({ artifact }) {
  const { currency } = useAiTeammateContext()
  const analytics = useAnalytics()
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false)

  const { columns: rawColumns, records, explainSteps } = artifact

  const hasRecords = records?.length > 0
  const hasColumnMetadata = rawColumns?.length > 0
  const firstRecord = first(records)

  const columnHeaders = useMemo(() => {
    if (hasColumnMetadata) {
      return rawColumns.map(col => col.label)
    }
    return firstRecord ? Object.keys(firstRecord) : []
  }, [rawColumns, firstRecord, hasColumnMetadata])

  const columnFormats = useMemo(() => columnHeaders.reduce((columnFormats, key, index) => {
    const fieldName = `col_${index}`
    if (hasColumnMetadata) {
      const columnMetadata = rawColumns[index]
      columnFormats[fieldName] = columnMetadata.format
    } else {
      columnFormats[fieldName] = isNumber(firstRecord?.[key]) ? VALUE_FORMATS.NUMBER : VALUE_FORMATS.STRING
    }
    return columnFormats
  }, {}), [columnHeaders, hasColumnMetadata, rawColumns, firstRecord])

  const columns = useMemo(() => {
    return columnHeaders.map((headerName, index) => {
      const fieldName = `col_${index}`
      const format = columnFormats[fieldName]
      const isNumericColumn = isNumberFormat(format)

      return {
        field: fieldName,
        headerName,
        type: getColumnType(format),
        flex: 1,
        minWidth: isNumericColumn ? 150 : 120,
        valueGetter: (value) => parseValue(value, format),
        valueFormatter: (value) => formatValueForDisplay(value, format, currency),
        hideable: true,
        exportable: true,
        pivotable: isPivotable(headerName, records),
        disableReorder: false,
      }
    })
  }, [columnHeaders, records, columnFormats, currency])

  const rows = useMemo(() => {
    if (!hasRecords) return []

    return records.map((record, rowIndex) => {
      // Build clean record with integer id and integer-based field names using reduce
      return columnHeaders.reduce((mappedRecord, columnHeader, colIndex) => {
        const fieldName = `col_${colIndex}`
        mappedRecord[fieldName] = record[columnHeader]
        return mappedRecord
      }, { id: rowIndex })
    })
  }, [hasRecords, records, columnHeaders])

  function toggleFullScreenTable() {
    if (!fullScreenTableOpen) {
      analytics.track(AI_TEAMMATE_EVENTS.TABLE_EXPANDED)
    }
    setFullScreenTableOpen(!fullScreenTableOpen)
  }

  return <>
    <Box className="ai-teammate-table-artifact">
      <div>Found {rows.length} {pluralize("result", rows.length)}:</div>
      <PreviewTable columns={columns} rows={rows} />
      <Button size="small" className="view-all-button" variant="outlined" color="primary" startIcon={<OpenInNew />} onClick={toggleFullScreenTable}>
        View all {rows.length} {pluralize("row", rows.length)}
      </Button>
    </Box>
    <FullSizeTable
      open={fullScreenTableOpen}
      onClose={toggleFullScreenTable}
      columns={columns}
      rows={rows}
      explainSteps={explainSteps}
    />
  </>
}

TableArtifact.propTypes = {
  artifact: TABLE_ARTIFACT.isRequired,
}
